import { useEffect, useMemo, useState } from 'react'

import Icon from '../Icon'

import SelectMenuItem from './SelectMenuItem'
import { SelectOption } from './types'

const defaultFilterOption = <TValue,>(searchQuery: string | null, option: SelectOption<TValue>) =>
  option.label.toLocaleLowerCase().includes(searchQuery?.toLocaleLowerCase() ?? '')

export const useFilteredOptions = <TValue,>(args: {
  isAsync: boolean
  filterOption?: (searchQuery: string, option: SelectOption<TValue>) => boolean
  searchQuery: string | null | undefined
  options: SelectOption<TValue>[]
}) => {
  const { isAsync, filterOption, options, searchQuery } = args
  return useMemo((): SelectOption<TValue>[] => {
    if (isAsync) {
      return options // the options are already filtered by the backend for async selects
    }
    const fn = filterOption ?? defaultFilterOption
    return options.filter((o) => fn(searchQuery ?? '', o))
  }, [filterOption, isAsync, options, searchQuery])
}

export const useCreateOption = (args: {
  activeIndex: number
  createActionIndex: number
  isCreatable: boolean | undefined
  onCreateOption: (() => void) | undefined
  searchQuery: string | null | undefined
  setActiveIndex: (index: number) => void
}) => {
  const {
    activeIndex,
    createActionIndex,
    isCreatable,
    onCreateOption,
    searchQuery,
    setActiveIndex,
  } = args

  return useMemo(() => {
    if (!isCreatable || !searchQuery) {
      return null
    }
    return (
      <SelectMenuItem
        id="create-option"
        key="create-option"
        onClick={onCreateOption}
        onMouseOver={() => setActiveIndex(createActionIndex)}
        {...(activeIndex === createActionIndex
          ? {
              ['data-selected']: true, // controls option remaining highlighted when submenu is expanded
              ['aria-selected']: true,
            }
          : {})}
      >
        <Icon.FaPlus /> {`Create "${searchQuery}"`}
      </SelectMenuItem>
    )
  }, [isCreatable, searchQuery, onCreateOption, activeIndex, createActionIndex, setActiveIndex])
}

export const useShowAsyncStartSearchingEmptyState = (args: {
  isAsync: boolean
  isLoading: boolean | null | undefined
  searchQuery: string | null | undefined
}) => {
  const { isAsync, isLoading, searchQuery } = args
  const [showAsyncStartSearchingEmptyState, setShowAsyncStartSearchingEmptyState] = useState(false)

  useEffect(() => {
    if (!isAsync) {
      setShowAsyncStartSearchingEmptyState(false)
      return
    }

    if (!searchQuery) {
      setShowAsyncStartSearchingEmptyState(true)
      return
    }
    if (isLoading && searchQuery) {
      setShowAsyncStartSearchingEmptyState(false)
      return
    }
  }, [isAsync, isLoading, searchQuery])

  return showAsyncStartSearchingEmptyState
}
